import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from "../../utils/constant";
import { useDispatch, useSelector } from "react-redux";
import { toastr } from 'react-redux-toastr';
import Pagination from "../Pagination/Pagination";
import $ from 'jquery';

const AwsCloud = () => {
    const orgId = useSelector((state) => state?.summary?.localClient?._id || state?.summary?.clients?.[0]?._id);
    const [cloudSecrets, setCloudSecrets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploadFile, setUploadFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [isUploadModal, setUploadModal] = useState(false);
    const [secretName, setSecretName] = useState('');
    const [platform, setPlatform] = useState('aws');
    const [scanningSecrets, setScanningSecrets] = useState(new Set());

    // Pagination state
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [totalItems, setTotalItems] = useState(0);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalSecrets: 0,
        limit: 10,
        hasNextPage: false,
        hasPreviousPage: false
    });

    // Fetch all cloud secrets with pagination
    const fetchCloudSecrets = async (pageNum = 1) => {
        if (!orgId) return;

        setLoading(true);
        try {
            const response = await axios.get(
                `${API_URL}/organisations/${orgId}/cloud-secrets?page=${pageNum}&limit=${limit}`
            );

            if (response.data.error) {
                toastr.error('Error', response.data.userMessage || 'Failed to fetch cloud secrets');
                return;
            }

            // Handle the nested response structure
            const { data, currentPage, totalPages, totalSecrets, limit: responseLimit, hasNextPage, hasPreviousPage } = response.data.data;

            setCloudSecrets(data);
            setTotalItems(totalSecrets);
            setPagination({
                currentPage,
                totalPages,
                totalSecrets,
                limit: responseLimit,
                hasNextPage,
                hasPreviousPage
            });

            // toastr.success('Success', 'Cloud secrets loaded successfully');
        } catch (error) {
            // console.error removed
            toastr.error('Error', error.response?.data?.userMessage || 'Failed to load cloud secrets');
        } finally {
            setLoading(false);
        }
    };

    // Delete a cloud secret
    const deleteCloudSecret = async (secretId) => {
        $.confirm({
            title: 'Delete Cloud Secret',
            content: 'Are you sure you want to delete this cloud secret? This action cannot be undone.',
            type: 'green',
            typeAnimated: true,
            buttons: {
                delete: {
                    text: 'Delete',
                    btnClass: 'btn-success',
                    action: async function () {
                        try {
                            const response = await axios.delete(
                                `${API_URL}/organisations/${orgId}/cloud-secrets/${secretId}`
                            );
                            // Refresh current page after deletion
                            fetchCloudSecrets(page);
                            toastr.success('Success', response.data.userMessage || 'Cloud secret deleted successfully');
                        } catch (error) {
                            // console.error removed
                            toastr.error('Error', error.response?.data?.userMessage || 'Failed to delete cloud secret');
                        }
                    }
                },
                cancel: {
                    text: 'Cancel',
                    btnClass: 'btn-secondary',
                    action: function () {
                        // Do nothing, just close the dialog
                    }
                }
            }
        });
    };

    // Upload cloud secret file
    const uploadCloudSecret = async () => {
        if (!uploadFile) {
            toastr.error('Error', 'Please select a file');
            return;
        }

        if (!secretName.trim()) {
            toastr.error('Error', 'Please enter a secret name');
            return;
        }

        setUploading(true);
        const formData = new FormData();
        formData.append('secretName', secretName);
        formData.append('platform', platform);
        formData.append('secretFile', uploadFile);

        try {
            const response = await axios.post(
                `${API_URL}/organisations/${orgId}/cloud-secrets/upload`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );

            toastr.success('Success', response.data.userMessage || 'Cloud secret uploaded successfully');
            setUploadFile(null);
            setSecretName('');
            setPlatform('aws');
            setUploadModal(false);
            // Clear the file input
            const fileInput = document.querySelector('input[type="file"]');
            if (fileInput) {
                fileInput.value = '';
            }
            setPage(1);
            fetchCloudSecrets(1); // Go to first page after upload
        } catch (error) {
            // console.error removed
            toastr.error('Error', error.response?.data?.userMessage || 'Failed to upload cloud secret');
        } finally {
            setUploading(false);
        }
    };

    // Initiate scan for a cloud secret
    const initiateScan = async (secretId) => {
        if (!orgId) {
            toastr.error('Error', 'Organization ID not found');
            return;
        }

        setScanningSecrets(prev => new Set(prev).add(secretId));

        try {
            const response = await axios.post(
                `${API_URL}/organisations/${orgId}/cloud-secrets/initiate-scan`,
                {
                    secretKeys: [secretId]
                }
            );

            if (response.data.error) {
                toastr.error('Error', response.data.userMessage || 'Failed to initiate scan');
                return;
            }

            toastr.success('Success', response.data.userMessage || 'Scan initiated successfully');
        } catch (error) {
            toastr.error('Error', error.response?.data?.userMessage || 'Failed to initiate scan');
        } finally {
            setScanningSecrets(prev => {
                const newSet = new Set(prev);
                newSet.delete(secretId);
                return newSet;
            });
        }
    };

    // Handle file selection
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        setUploadFile(file);
    };



    // Load cloud secrets when orgId is available
    useEffect(() => {
        if (orgId) {
            fetchCloudSecrets(page);
        }
    }, [orgId, page, limit]);

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-lg-12">
                    <div className="card">
                        <h4 className="welcome-text mt-4 mb-3">Cloud Account Management</h4>
                        <div className="col-md-12 text-right mb-4">
                            <button
                                className="btn btn-primary"
                                onClick={() => setUploadModal(true)}
                            >
                                <i className="fa fa-plus mr-2"></i>
                                Add Cloud Secret
                            </button>
                        </div>
                        <div className="card-body">
                            {/* Cloud Secrets List */}
                            <div className="row">
                                <div className="col-md-12">
                                    <div className="card">
                                        <div className="d-flex justify-content-between align-items-center mb-0">
                                            <div>
                                                <h5 className="welcome-textSmall mb-0">Cloud Account List</h5>
                                                <small className="text-muted">Click the Scan button to initiate security scanning for each cloud account. Some accounts may not support IP scanning.</small>
                                            </div>
                                        </div>
                                        <div className="card-body">
                                            {loading ? (
                                                <div className="text-center">
                                                    <div className="spinner-border" role="status">
                                                        <span className="sr-only">Loading...</span>
                                                    </div>
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="table-responsive">
                                                        <table className="table table-custom spacing5 mb-0">
                                                            <thead>
                                                                <tr>
                                                                    <th>Secret Name</th>
                                                                    <th>Platform</th>
                                                                    <th>Secret Key</th>
                                                                    <th>Created Date</th>
                                                                    <th>Scan Status</th>
                                                                    <th>Actions</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {cloudSecrets.length > 0 ? (
                                                                    cloudSecrets.map((secret) => (
                                                                        <tr key={secret._id}>
                                                                            <td>{secret.secretName}</td>
                                                                            <td>
                                                                                <span className={`badge badge-${secret.platform === 'gcp' ? 'primary' : secret.platform === 'aws' ? 'warning' : 'info'}`}>
                                                                                    {secret.platform?.toUpperCase()}
                                                                                </span>
                                                                            </td>
                                                                            <td>
                                                                                <span className="text-monospace">
                                                                                    {secret.secretKey}
                                                                                </span>
                                                                            </td>
                                                                            <td>
                                                                                {secret.createdAt ? new Date(secret.createdAt).toLocaleDateString() : 'N/A'}
                                                                            </td>
                                                                            <td>
                                                                                <span className={`badge badge-${secret.canInitiateIPScan ? 'success' : 'warning'}`}>
                                                                                    {secret.canInitiateIPScan ? 'Available' : 'Not Available'}
                                                                                </span>
                                                                            </td>
                                                                            <td>
                                                                                <div className="btn-group" role="group">
                                                                                    <button
                                                                                        onClick={() => initiateScan(secret._id)}
                                                                                        disabled={scanningSecrets.has(secret._id) || !secret.canInitiateIPScan}
                                                                                        title={secret.canInitiateIPScan ? "Initiate Scan" : "IP Scan not available for this account"}
                                                                                        className={`btn btn-sm mr-2 ${secret.canInitiateIPScan ? 'btn-primary' : 'btn-secondary'}`}
                                                                                    >
                                                                                        {scanningSecrets.has(secret._id) ? (
                                                                                            <>
                                                                                                <span className="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
                                                                                                Scanning...
                                                                                            </>
                                                                                        ) : (
                                                                                            <>
                                                                                                <i className="fa fa-search mr-1"></i>
                                                                                                Scan
                                                                                            </>
                                                                                        )}
                                                                                    </button>
                                                                                    {!secret.canInitiateIPScan && (
                                                                                        <span className="badge badge-warning ml-1" title="IP Scan not available">
                                                                                            <i className="fa fa-info-circle"></i>
                                                                                        </span>
                                                                                    )}
                                                                                    <button
                                                                                        className="btn btn-danger btn-sm"
                                                                                        onClick={() => deleteCloudSecret(secret._id)}
                                                                                        title="Delete Secret"
                                                                                    >
                                                                                        <i className="fa fa-trash mr-1"></i>
                                                                                        Delete
                                                                                    </button>
                                                                                </div>
                                                                            </td>
                                                                        </tr>
                                                                    ))
                                                                ) : (
                                                                    <tr>
                                                                        <td colSpan="6" className="text-center">
                                                                            No cloud secrets found
                                                                        </td>
                                                                    </tr>
                                                                )}
                                                            </tbody>
                                                        </table>
                                                    </div>

                                                    {/* Pagination */}
                                                    <div className="d-flex justify-content-between align-content-center">
                                                        <div className="dataTables_paginate">
                                                            {totalItems > 0 && (
                                                                <Pagination
                                                                    className="pagination-bar"
                                                                    currentPage={page}
                                                                    totalCount={totalItems}
                                                                    pageSize={limit}
                                                                    onPageChange={(pageNum) => {
                                                                        setPage(pageNum);
                                                                    }}
                                                                />
                                                            )}
                                                        </div>
                                                        {totalItems > 10 && (
                                                            <select
                                                                value={limit}
                                                                onChange={(e) => {
                                                                    setLimit(e.target.value);
                                                                    setPage(1);
                                                                }}
                                                                className="form-control limitSelectBox"
                                                            >
                                                                <option value={10}>10 per page</option>
                                                                <option value={25}>25 per page</option>
                                                                <option value={50}>50 per page</option>
                                                                <option value={100}>100 per page</option>
                                                            </select>
                                                        )}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Upload Modal */}
            <div className={`modal fade Form-Wizard-modal ${isUploadModal ? "d-block show" : ""}`}>
                <div className="modal-dialog modal-lg" id="uploadModal">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Add Cloud Secret</h5>
                            <button
                                type="button"
                                data-dismiss="modal"
                                aria-label="Close"
                                onClick={() => {
                                    setUploadModal(false);
                                    setUploadFile(null);
                                    setSecretName('');
                                    setPlatform('gcp');
                                    // Clear the file input
                                    const fileInput = document.querySelector('input[type="file"]');
                                    if (fileInput) {
                                        fileInput.value = '';
                                    }
                                }}
                            >
                                <span aria-hidden="true">×</span>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="row">
                                <div className="col-md-12">
                                    <div className="form-group">
                                        <label>Secret Name *</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Enter secret name"
                                            value={secretName}
                                            onChange={(e) => setSecretName(e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-12">
                                    <div className="form-group">
                                        <label>Secret File *</label>
                                        <input
                                            type="file"
                                            className="form-control"
                                            onChange={handleFileChange}
                                            accept=".json,.txt,.pem,.key"
                                        />
                                        <small className="form-text text-muted">
                                            Supported formats: .json
                                        </small>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => {
                                    setUploadModal(false);
                                    setUploadFile(null);
                                    setSecretName('');
                                    setPlatform('aws');
                                    // Clear the file input
                                    const fileInput = document.querySelector('input[type="file"]');
                                    if (fileInput) {
                                        fileInput.value = '';
                                    }
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={uploadCloudSecret}
                                disabled={uploading || !uploadFile || !secretName.trim()}
                            >
                                {uploading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                                        Uploading...
                                    </>
                                ) : (
                                    'Upload Secret'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AwsCloud;
